import * as Sentry from '@sentry/react'
import { reaction, makeAutoObservable, type IReactionDisposer } from 'mobx'
import {
  APP_ENV,
  SENTRY_RELEASE,
  SENTRY_PROFILES_SAMPLE_RATE,
  SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
  SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  SENTRY_TRACES_SAMPLE_RATE,
} from 'shared/config'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { getToken } from 'entities/Auth/lib/setToken'

class SentryApi {
  private _disposeUser: IReactionDisposer | null = null
  private _disposeOrganizationId: IReactionDisposer | null = null
  private _disposeOrganizationAgencyId: IReactionDisposer | null = null

  constructor() {
    makeAutoObservable(this)
  }

  identify = (entities: IEntitiesStore) => {
    if (!Sentry.isInitialized()) return

    const { usersStore, organizationStore } = entities

    this._disposeUser?.()
    this._disposeUser = reaction(
      () => usersStore.user,
      (user) => {
        if (!user) return

        Sentry.setUser({
          id: user.id,
          email: user.email,
          username: user.full_name,
        })
      }
    )

    this._disposeOrganizationId?.()
    this._disposeOrganizationId = reaction(
      () => organizationStore.id,
      (organizationId) => Sentry.setTag('organization.id', organizationId ?? 'null')
    )

    this._disposeOrganizationAgencyId?.()
    this._disposeOrganizationAgencyId = reaction(
      () => organizationStore.agency_id,
      (agencyId) => Sentry.setTag('agency.id', agencyId ?? 'null')
    )
  }
}

export const sentry = new SentryApi()
export const initSentry = (dsn?: string) => {
  if (!dsn) return
  if (Sentry.isInitialized()) return

  let hipaa = false
  const token = getToken()

  if (token) {
    try {
      const [, tokenData] = token.split('.')
      const parsedData = JSON.parse(atob(tokenData))

      hipaa = parsedData.hipaa
    } catch (e) {
      console.log(e)
    }
  }

  if (hipaa) {
    window.userpilot?.destroy?.()
    Sentry.getReplay()?.stop()

    return
  }

  Sentry.init({
    dsn,
    release: SENTRY_RELEASE,
    environment: APP_ENV,

    tracePropagationTargets: ['int/v5/core'],

    replaysSessionSampleRate: SENTRY_REPLAYS_SESSION_SAMPLE_RATE
      ? Number(SENTRY_REPLAYS_SESSION_SAMPLE_RATE)
      : 1.0,
    replaysOnErrorSampleRate: SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE
      ? Number(SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE)
      : 1.0,
    tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE ? Number(SENTRY_TRACES_SAMPLE_RATE) : 0.1,
    profilesSampleRate: SENTRY_PROFILES_SAMPLE_RATE ? Number(SENTRY_PROFILES_SAMPLE_RATE) : 0.1,

    beforeSend(event, hint) {
      const error = hint?.originalException

      if (error instanceof Error) {
        const msg = error.message || ''
        const name = error.name || ''

        // PusherError — network WebSocket disconnects, not actionable
        if (name === 'PusherError' || msg.includes('PusherError')) {
          return Math.random() < 0.05 ? event : null
        }

        // Twilio connection/transport errors — depend on user network and Twilio infra
        if (
          name === 'ConnectionError' ||
          name === 'TransportError' ||
          msg.includes('ConnectionError') ||
          msg.includes('TransportError') ||
          msg.includes('31005') ||
          msg.includes('31009') ||
          msg.includes('53000')
        ) {
          return Math.random() < 0.05 ? event : null
        }

        // WebSocket unavailable — infrastructure-level, not actionable
        if (msg.includes('Websocket:Unavailable') || msg.includes('EventsConnection')) {
          return Math.random() < 0.05 ? event : null
        }
      }

      return event
    },

    integrations: [
      // Mask typed inputs and do not capture request/response bodies (SWR-24403): they carry tokens and credentials.
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,

        networkRequestHeaders: ['Cache-Control'],
        networkResponseHeaders: ['Referrer-Policy'],
      }),
      Sentry.replayCanvasIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
    ],
  })
}
